import React, { useEffect, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { deriveMapStateFromConfig, TWC } from '@tmapy/beetlejuice-utils';
import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';
import {
  BtnBar,
  CtaBtn,
  DangerAlert,
  FormItem,
  FormRow,
  Input,
  SecondaryBtn,
  Textarea,
  WaitingAlert,
} from '@tmapy/style-guide';

import { useDocsRetrieveResource } from '../../openapi/operations/docsRetrieve';
import { docsGetRozsahAktualizacniZmenyExtentPartialUpdate } from '../../openapi/operations/docsGetRozsahAktualizacniZmenyExtentPartialUpdate';
import { resolveMapUrl } from '../utils/resolveMapUrl';
import { MapReviewButtons } from './MapReviewButtons';
import { dmvsDtiDocsClosePartialUpdate } from '../../openapi/operations/dmvsDtiDocsClosePartialUpdate';
import { dmvsDtiDocsImportDocCreate } from '../../openapi/operations/dmvsDtiDocsImportDocCreate';
import { dmvsDtiDocsCloseInterruptPartialUpdate } from '../../openapi/operations/dmvsDtiDocsCloseInterruptPartialUpdate';
import { dmvsDtiDocsImportInterruptPartialUpdate } from '../../openapi/operations/dmvsDtiDocsImportInterruptPartialUpdate';
import { dmvsDtiDocsNotifyClosePartialUpdate } from '../../openapi/operations/dmvsDtiDocsNotifyClosePartialUpdate';
import { dmvsDtiDocsRejectPartialUpdate } from '../../openapi/operations/dmvsDtiDocsRejectPartialUpdate';

import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { MapWrapper } from '../../app/components/MapWrapper';
import { MapWithAreas } from '../../areas/components/MapWithAreas';
import { LogList } from '../../logs/components/LogList';
import { HistoryList } from '../../history/components/HistoryList';

import { mapConfig } from '../../manager/mapConfig';

import type { InputProps } from '../../hooks/useInput';
import {
  SSE_TYPE,
  useAuth,
  useLogDownloadLink,
  useInput,
  useSelector,
  useSSE,
  useAttachments,
  useAction,
  toBtnQueryState,
} from '../../hooks';
import { QUERY_STATE } from '../../constants';

import {
  DOC_STATE,
  DOCDETAIL_LOAD_SUCCESS,
  DOCDETAIL_LOAD_WAITING,
  DocFormTypes,
  usableItemsForFormType,
} from '../constants';

import { docFormMessages, docTitleMessages } from './docsMessages';
import { docEditButtonsMsg, docEditMessages } from './docEditMessages';
import { defaultValues } from './defaultValues';
import { DocAttachments } from './DocAttachments';
import { DocBadge } from './DocBadge';
import { DocDownloadFiles } from './DocDownloadFiles';
import { DocInternalInfo } from './DocInternalInfo';
import { getErrorMessages, ResponseErrorMessage } from './ResponseErrorMessage';

import './Doc.scss';

export type DocEditContainerProps = {
  docId?: number;
};

export const DocDmvsDti: React.FC<DocEditContainerProps> = ({ docId }) => {
  useSSE(SSE_TYPE.DOCS, docId);
  const auth = useAuth();

  const formatMessage = useMessage();
  const dispatch = useDispatch();

  const reviewerConfigUrl = useSelector((state) => state.app.config.reviewer.url);
  const reviewer3dConfigUrl = useSelector((state) => state.app.config.reviewer.url3d);

  const [closeQueryState, setCloseQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [importQueryState, setImportQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [closeInterruptQueryState, setCloseInterruptQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [importInterruptQueryState, setImportInterruptQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [notifyCloseQueryState, setNotifyCloseQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [rejectQueryState, setRejectQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [rejectErrors, setRejectErrors] = useState<{ code: string; message: string }[] | null>(
    null,
  );

  const stateData = useSelector((state) => state.doc.data);
  const attachmentTypes = useAttachments(stateData.attachments);
  const resource = useDocsRetrieveResource(docId ? { id: docId } : null);

  const data = useMemo(() => {
    const cachedData = stateData.id === docId ? stateData : resource.data;
    return docId && cachedData ? cachedData : { ...defaultValues };
  }, [resource.data, stateData, docId]);

  const [mapDataExtent, setMapDataExtent] = useState<number[]>([]);
  const mapState = useMemo(() => deriveMapStateFromConfig({ map: mapConfig() } as TWC), []);
  const mapExtent = useMemo(() => {
    return resource.data?.rozsah_aktualizacni_zmeny_extent.length
      ? resource.data.rozsah_aktualizacni_zmeny_extent
      : mapDataExtent.length
        ? mapDataExtent
        : undefined;
  }, [resource.data?.rozsah_aktualizacni_zmeny_extent, mapDataExtent]);

  const formType: DocFormTypes = 'dmvsDti';
  const usableItems = usableItemsForFormType[formType];

  const acl = useMemo(
    () => ({
      docsActionNotifyClose: auth.hasPermission(
        'DmvsDtiDocs',
        'action.dmvs_dti_notify_close',
        data,
      ),
      docsActionImportInterrupt: auth.hasPermission('DmvsDtiDocs', 'action.import_interrupt', data),
      docsActionCloseInterrupt: auth.hasPermission(
        'DmvsDtiDocs',
        'action.dmvs_dti_close_interrupt',
        data,
      ),
      docsActionClose: auth.hasPermission('DmvsDtiDocs', 'action.dmvs_dti_close', data),
      docsActionImport: auth.hasPermission('DmvsDtiDocs', 'action.dmvs_dti_import_doc', data),
      docsActionShowLog: auth.hasPermission('DmvsDtiDocs', 'action.logs', data),
      docsActionReject: auth.hasPermission('DmvsDtiDocs', 'action.reject', data),

      docsActionMapReview: auth.hasPermission('DmvsDtiDocs', 'action.map_review', data),
      docsAttachmentsISDMVS: auth.hasPermission('DmvsDtiDocs', 'attachments_isdmvs', data),

      // download section
      docsActionDownloadCloseLog: auth.hasPermission('DmvsDtiDocs', 'download_close_log', data),
      docsActionDownloadImportLog: auth.hasPermission('DmvsDtiDocs', 'download_import_log', data),
    }),
    [auth, data],
  );

  const [showLogDownloadError, setShowLogDownloadError] = useState(false);
  const handleShowDownloadLogError = (visible: boolean) => {
    setShowLogDownloadError(visible);
  };

  const closeLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_close_log',
    handleShowDownloadLogError,
  );

  const importLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_import_log',
    handleShowDownloadLogError,
  );

  // MAP REVIEWER
  useEffect(() => {
    (async () => {
      if (resource.data?.rozsah_aktualizacni_zmeny_extent?.length) {
        setMapDataExtent(resource.data.rozsah_aktualizacni_zmeny_extent);
        return;
      }

      if (docId && acl.docsActionMapReview) {
        const result = await docsGetRozsahAktualizacniZmenyExtentPartialUpdate({
          query: { id: docId },
          request: {},
        });
        if (result.ok) {
          setMapDataExtent(result.data.rozsah_aktualizacni_zmeny_extent);
        }
      }
    })();
  }, [docId, resource.data, acl.docsActionMapReview]);

  const hashParams = useMemo(
    () => (mapDataExtent.length ? `#bbox=${mapDataExtent}` : ''),
    [mapDataExtent],
  );

  const canMapReview = !!docId && acl.docsActionMapReview;
  const reviewUrl = resolveMapUrl(reviewerConfigUrl, `-tidi/${docId}/${hashParams}`);
  const review3dUrl = resolveMapUrl(reviewer3dConfigUrl, `-tidi/?doc_id=${docId}`);

  // ACTIONS
  useEffect(() => {
    if (!resource.data) {
      dispatch({
        type: DOCDETAIL_LOAD_WAITING,
      });
    } else {
      dispatch({
        type: DOCDETAIL_LOAD_SUCCESS,
        data: resource.data,
      });
    }
  }, [resource.data, dispatch]);

  const handleImportBtnClick = () => {
    doAction(dmvsDtiDocsImportDocCreate, setImportQueryState);
  };
  const handleCloseBtnClick = () => {
    doAction(dmvsDtiDocsClosePartialUpdate, setCloseQueryState);
  };
  const handleRejectBtnClick = async () => {
    const response = await doAction(dmvsDtiDocsRejectPartialUpdate, setRejectQueryState);
    if (response && !response.ok) {
      setRejectErrors(getErrorMessages(response.error?.data));
    }
  };
  const handleCloseInterruptBtnClick = () => {
    doAction(dmvsDtiDocsCloseInterruptPartialUpdate, setCloseInterruptQueryState);
  };
  const handleImportInterruptBtnClick = () => {
    doAction(dmvsDtiDocsImportInterruptPartialUpdate, setImportInterruptQueryState);
  };
  const handleNotifyCloseBtnClick = () => {
    doAction(dmvsDtiDocsNotifyClosePartialUpdate, setNotifyCloseQueryState);
  };

  const actionQuery = useMemo(() => ({ id: docId }), [docId]);
  const { run: doAction, isActionRunning } = useAction({
    query: actionQuery,
    onRefetch: resource.refetch,
  });

  // HOOK FOR INPUTS
  const dmvs_id_podani = useInput<InputProps>({
    name: 'dmvs_id_podani',
    isDisabled: true,
    initialValue: data.dmvs_id_podani,
    label: formatMessage(docFormMessages.dmvs_id_podani),
  });
  const dmvs_source = useInput<InputProps>({
    name: 'dmvs_source',
    isDisabled: true,
    initialValue: data.dmvs_source,
    label: formatMessage(docFormMessages.dmvs_source),
  });

  const description = useInput<InputProps>({
    name: 'description',
    isDisabled: true,
    initialValue: data.description,
    label: formatMessage(docFormMessages.description),
  });
  const dmvs_identifikace_zmeny = useInput<InputProps>({
    name: 'dmvs_identifikace_zmeny',
    isDisabled: true,
    initialValue: data.dmvs_identifikace_zmeny,
    label: formatMessage(docFormMessages.dmvs_identifikace_zmeny),
  });
  const subjekt_id = useInput<InputProps>({
    name: 'subjekt_id',
    isDisabled: true,
    initialValue: data.subjekt?.id_subjektu,
    label: formatMessage(docFormMessages.subjekt_id),
  });
  const subjekt_jmeno = useInput<InputProps>({
    name: 'subjekt_jmeno',
    isDisabled: true,
    initialValue: data.subjekt?.jmeno,
    label: formatMessage(docFormMessages.subjekt_jmeno),
  });
  const editor_subjekt_id = useInput<InputProps>({
    name: 'editor_subjekt_id',
    isDisabled: true,
    initialValue: data.editor_subjekt?.id_subjektu,
    label: formatMessage(docFormMessages.editor_subjekt_id),
  });
  const editor_subjekt_jmeno = useInput<InputProps>({
    name: 'editor_subjekt_jmeno',
    isDisabled: true,
    initialValue: data.editor_subjekt?.jmeno,
    label: formatMessage(docFormMessages.editor_subjekt_jmeno),
  });

  return (
    <MainContent width='full' key={`${resource.data?.id}`}>
      {(resource.queryState === QUERY_STATE.SUCCESS || !docId) && (
        <article>
          <div className='dmx-doc--head sg-u-vs-2'>
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
                <PageTitle>
                  {formatMessage.lookup(docTitleMessages, formType, {
                    id: data.id || 'new',
                  })}
                </PageTitle>
                {data.state && (
                  <DocBadge
                    docState={data.state}
                    flow={data.flow}
                    rejectedBySubjekty={data.rejected_by_subjekty}
                  />
                )}
              </header>
              <BtnBar>
                {!!docId && acl.docsActionNotifyClose && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(notifyCloseQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleNotifyCloseBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCloseInterrupt && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(closeInterruptQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCloseInterruptBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnInterrupt} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionImportInterrupt && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(importInterruptQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleImportInterruptBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnInterrupt} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionClose && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(closeQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCloseBtnClick}
                  >
                    {data.state === DOC_STATE.CLOSE_INTERRUPTED ||
                    data.state === DOC_STATE.CLOSE_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnClose} />
                    )}
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionReject && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(rejectQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleRejectBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnReject} />
                  </SecondaryBtn>
                )}

                {!!docId &&
                  acl.docsActionImport &&
                  (data.state === DOC_STATE.IMPORT_INTERRUPTED ||
                  data.state === DOC_STATE.IMPORT_FAILED ? (
                    <SecondaryBtn
                      queryState={toBtnQueryState(importQueryState)}
                      isDisabled={isActionRunning}
                      onClick={handleImportBtnClick}
                    >
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    </SecondaryBtn>
                  ) : (
                    <CtaBtn
                      queryState={toBtnQueryState(importQueryState)}
                      isDisabled={isActionRunning}
                      onClick={handleImportBtnClick}
                    >
                      <FormattedMessage {...docEditButtonsMsg.docBtnImport} />
                    </CtaBtn>
                  ))}
              </BtnBar>
            </div>
            {showLogDownloadError && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.fileDownloadError} />
              </DangerAlert>
            )}
            {(resource.queryState === QUERY_STATE.WAITING ||
              closeQueryState === QUERY_STATE.WAITING ||
              importQueryState === QUERY_STATE.WAITING ||
              closeInterruptQueryState === QUERY_STATE.WAITING ||
              importInterruptQueryState === QUERY_STATE.WAITING ||
              rejectQueryState === QUERY_STATE.WAITING ||
              notifyCloseQueryState === QUERY_STATE.WAITING) && (
              <WaitingAlert>
                <FormattedMessage {...docEditMessages.queryStateWaiting} />
              </WaitingAlert>
            )}
            {resource.queryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.loadQueryStateFail} />
              </DangerAlert>
            )}
            {(closeQueryState === QUERY_STATE.FAIL ||
              notifyCloseQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.closeQueryStateFail} />
              </DangerAlert>
            )}
            {importQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.importQueryStateFail} />
              </DangerAlert>
            )}
            {(closeInterruptQueryState === QUERY_STATE.FAIL ||
              importInterruptQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.interruptQueryStateFail} />
              </DangerAlert>
            )}
            {rejectQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.rejectQueryStateFail} />
                {rejectErrors?.map((error, index) => (
                  <React.Fragment key={index}>
                    <br />
                    <ResponseErrorMessage error={error} />
                  </React.Fragment>
                ))}
              </DangerAlert>
            )}
          </div>
          <div className='dmx-doc--cols'>
            <div className='sg-u-vs-4'>
              <div className='sg-u-vs-4'>
                <section>
                  <h3 className='dmx-doc--attributesTitle sg-u-type-h2 sg-a-ml-2'>&nbsp;</h3>
                  <div className='sg-u-box sg-a-p-2 sg-u-vs-2 sg-a-mt-1'>
                    {usableItems?.includes('dmvs_id_podani') && (
                      <FormRow>
                        <FormItem {...dmvs_id_podani}>
                          <Input {...dmvs_id_podani} />
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('dmvs_source') && (
                      <FormRow>
                        <FormItem {...dmvs_source}>
                          <Input {...dmvs_source} />
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('subjekt') && (
                      <>
                        <FormRow>
                          <FormItem {...subjekt_id}>
                            <Input {...subjekt_id} />
                          </FormItem>
                        </FormRow>
                        <FormRow>
                          <FormItem {...subjekt_jmeno}>
                            <Input {...subjekt_jmeno} />
                          </FormItem>
                        </FormRow>
                      </>
                    )}
                    {usableItems?.includes('editor_subjekt') && (
                      <>
                        <FormRow>
                          <FormItem {...editor_subjekt_id}>
                            <Input {...editor_subjekt_id} />
                          </FormItem>
                        </FormRow>
                        <FormRow>
                          <FormItem {...editor_subjekt_jmeno}>
                            <Input {...editor_subjekt_jmeno} />
                          </FormItem>
                        </FormRow>
                      </>
                    )}
                    {usableItems?.includes('description') && (
                      <FormRow>
                        <FormItem {...description}>
                          <Textarea {...description} />
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('dmvs_identifikace_zmeny') && (
                      <FormRow>
                        <FormItem {...dmvs_identifikace_zmeny}>
                          <Input {...dmvs_identifikace_zmeny} />
                        </FormItem>
                      </FormRow>
                    )}
                  </div>
                </section>
              </div>

              {resource.data && <DocInternalInfo doc={resource.data} />}

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...docFormMessages.attachments} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-u-vs-3'>
                  <section>
                    {usableItems?.includes('attachments') && (
                      <section>
                        <h3 className='sg-u-type-h2 sg-a-ml-2'>
                          <FormattedMessage {...docFormMessages.attachments_docs} />
                        </h3>
                        <div className='sg-u-box sg-a-d-ib sg-a-mw-100% sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                          <DocAttachments
                            doc={resource?.data}
                            prejimkaType={data.prejimka_type}
                            attachments={attachmentTypes.regular}
                          />
                        </div>
                      </section>
                    )}
                    {usableItems?.includes('attachments_isdmvs') && acl.docsAttachmentsISDMVS && (
                      <section className='sg-a-mt-2'>
                        <h3 className='sg-u-type-h2 sg-a-ml-2'>
                          <FormattedMessage {...docFormMessages.attachments_isdmvs} />
                        </h3>
                        <div className='sg-u-box sg-a-d-ib sg-a-mw-100% sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                          <DocAttachments
                            doc={resource?.data}
                            prejimkaType={data.prejimka_type}
                            attachments={attachmentTypes.isdmvs}
                          />
                        </div>
                      </section>
                    )}
                    <section className='sg-a-mt-2'>
                      <DocDownloadFiles
                        title={<FormattedMessage {...docFormMessages.attachments_system} />}
                        docId={docId}
                        items={[
                          {
                            permission: acl.docsActionDownloadCloseLog,
                            link: closeLogDownloadLink,
                            label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadLog} />,
                            ext: 'zip',
                          },
                          {
                            permission: acl.docsActionDownloadImportLog,
                            link: importLogDownloadLink,
                            label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadLog} />,
                            ext: 'zip',
                          },
                        ]}
                      />
                    </section>
                  </section>
                </div>
              </section>
            </div>
            <div className='dmx-doc--rightCol sg-u-vs-4'>
              {acl.docsActionMapReview && mapState && (
                <section className='sg-a-f-a sg-a-d-f sg-a-fd-c sg-a-rg-1'>
                  <div className='sg-a-d-f sg-a-jc-sb sg-a-ai-c sg-a-fw-w sg-a-cg-2'>
                    <h3 className='sg-u-type-h2 sg-a-ml-2'>
                      <FormattedMessage {...docFormMessages.docAreaTitle} />
                    </h3>
                    {canMapReview && (
                      <MapReviewButtons reviewUrl={reviewUrl} review3dUrl={review3dUrl} />
                    )}
                  </div>
                  <div className='sg-u-box sg-a-p-2 sg-a-f-a'>
                    <MapWrapper>
                      <MapWithAreas state={mapState} extent={mapExtent} />
                    </MapWrapper>
                  </div>
                </section>
              )}

              {acl.docsActionShowLog && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...docFormMessages.logs} />
                  </h3>
                  <LogList objectId={docId!} resourceType={'doc'} />
                </section>
              )}
              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                  <FormattedMessage {...docFormMessages.history} />
                </h3>
                <HistoryList history={data.history} />
              </section>
            </div>
          </div>
        </article>
      )}
    </MainContent>
  );
};
